/**
 * ForBusinessPage
 * Explains the Wildy offer to nature and wildlife operators as it really works today:
 * a free listing that sends travellers to the operator's own booking page.
 * The same facts back the Playbook tab in the internal tool; keep the two in step.
 *
 * Page order follows the operator's questions: what is it (hero) → where is Wildy now and what may
 * change (stage) → is it real (live operators) → what do I have to do (how it works) → what do I get
 * (cost) → details (FAQ)
 * → join. Joining is one email to info@wildy.world (no form, on purpose); every CTA scrolls to it.
 */

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Check, Mail, MapPin, MousePointerClick, Minus, Sprout, Telescope, TrendingUp, Wand2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SectionHeader } from '@/components/ui/section-header';
import { Header } from '@/components/Header';
import { Footer } from '@/components/homepage/Footer';
import { ForBusinessHero } from '@/components/forbusiness/ForBusinessHero';
import { LiveOperators } from '@/components/forbusiness/LiveOperators';
import { EmailUsCard } from '@/components/shared/EmailUsCard';
import { cn } from '@/lib/utils';

const scrollTo = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const asList = (value: unknown): string[] => (Array.isArray(value) ? value as string[] : []);

function FactList({ items, tone }: { items: string[]; tone: 'yes' | 'no' }) {
  const Icon = tone === 'yes' ? Check : Minus;
  return (
    <ul className="space-y-3">
      {items.map(item => (
        <li key={item} className={`flex gap-3 leading-relaxed ${tone === 'yes' ? 'text-slate-700' : 'text-slate-600'}`}>
          <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${tone === 'yes' ? 'text-action-500' : 'text-slate-400'}`} aria-hidden />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4 },
};

export function ForBusinessPage() {
  const { t } = useTranslation('public', { keyPrefix: 'forbusiness' });

  const stageSteps = [
    { key: 'now', icon: Sprout },
    { key: 'next', icon: TrendingUp },
    { key: 'later', icon: Telescope },
  ] as const;
  const steps = [
    { key: 'yes',   icon: Mail },
    { key: 'build', icon: Wand2 },
    { key: 'book',  icon: MousePointerClick },
  ] as const;

  const getItems = asList(t('offer.get.items', { returnObjects: true }));
  const notItems = asList(t('offer.not.items', { returnObjects: true }));
  const promises = asList(t('join.promises', { returnObjects: true }));
  const faqs = (t('faq.items', { returnObjects: true }) as { q: string; a: string }[]) ?? [];

  // The header's "Join Wildy" links to /for-business#join; React Router doesn't scroll to hashes.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const timer = setTimeout(() => scrollTo(id), 150);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <ForBusinessHero videoSrc="/video/hero-section-brume-video-background.mp4">
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm mb-5 sm:mb-6">
                <MapPin className="w-4 h-4" aria-hidden />
                {t('hero.badge')}
              </span>
              <h1
                className="text-4xl sm:text-6xl font-bold text-white tracking-tight leading-[1.05] [text-wrap:balance]"
                style={{ textShadow: '0 4px 20px rgba(0,0,0,0.45)' }}
              >
                {t('hero.title.part1')}
              </h1>
              <p
                className="mt-5 sm:mt-6 mx-auto max-w-2xl text-lg sm:text-xl text-white/95 leading-relaxed"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
              >
                {t('hero.description')}
              </p>
              <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button size="lg" onClick={() => scrollTo('join')} className="group w-full sm:w-auto">
                  {t('hero.cta.primary')}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => scrollTo('how-it-works')}
                  className="w-full sm:w-auto border-white/50 bg-white/10 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm focus-visible:ring-white"
                >
                  {t('hero.cta.secondary')}
                </Button>
              </div>
            </motion.div>
          </div>
      </ForBusinessHero>

      {/* Stage: where Wildy is today and what may change, said up front so nothing feels hidden */}
      <section id="stage" aria-labelledby="stage-title" className="py-16 sm:py-24 bg-brand-50 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader id="stage-title" eyebrow={t('stage.eyebrow')} title={t('stage.title')} description={t('stage.description')} />
          <ol className="grid gap-4 sm:gap-6 md:grid-cols-3">
            {stageSteps.map(({ key, icon: Icon }, i) => (
              <motion.li key={key} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }}
                className={cn('rounded-lg border bg-white p-6 shadow-sm', i === 0 ? 'border-primary ring-1 ring-primary/20' : 'border-brand-200')}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 text-primary" aria-hidden>
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="text-sm font-semibold uppercase tracking-wider text-primary">{t(`stage.steps.${key}.label`)}</span>
                </div>
                <h3 className="text-lg font-semibold text-brand-900 mb-2">{t(`stage.steps.${key}.title`)}</h3>
                <p className="text-slate-600 leading-relaxed">{t(`stage.steps.${key}.description`)}</p>
              </motion.li>
            ))}
          </ol>
          <p className="mt-8 text-center text-sm font-semibold text-primary">{t('stage.signature')}</p>
        </div>
      </section>

      {/* Proof: real operators live today (renders nothing if the API is unavailable) */}
      <LiveOperators />

      {/* How it works */}
      <section id="how-it-works" aria-labelledby="how-title" className="py-16 sm:py-24 bg-white scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader id="how-title" eyebrow={t('how.eyebrow')} title={t('how.title')} />
          <ol className="grid gap-4 sm:gap-6 md:grid-cols-3">
            {steps.map(({ key, icon: Icon }, i) => (
              <motion.li key={key} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 text-primary font-bold" aria-hidden>
                    {i + 1}
                  </span>
                  <Icon className="w-5 h-5 text-action-500" aria-hidden />
                </div>
                <h3 className="text-lg font-semibold text-brand-900 mb-2">{t(`how.steps.${key}.title`)}</h3>
                <p className="text-slate-600 leading-relaxed">{t(`how.steps.${key}.description`)}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* What it costs today; the longer-term plan is in the Stage section above */}
      <section id="pricing" aria-labelledby="pricing-title" className="py-16 sm:py-24 bg-brand-50 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader id="pricing-title" eyebrow={t('offer.eyebrow')} title={t('offer.title')} description={t('offer.description')} />
          <motion.div {...fadeUp} className="grid gap-4 sm:gap-6 md:grid-cols-[3fr_2fr]">
            <div className="rounded-lg border border-brand-200 bg-white p-6 sm:p-8 shadow-sm">
              <h3 className="text-lg font-semibold text-brand-900 mb-5">{t('offer.get.title')}</h3>
              <FactList items={getItems} tone="yes" />
            </div>
            <div className="rounded-lg border border-slate-200 bg-white/70 p-6 sm:p-8">
              <h3 className="text-lg font-semibold text-slate-700 mb-5">{t('offer.not.title')}</h3>
              <FactList items={notItems} tone="no" />
            </div>
          </motion.div>

        </div>
      </section>

      {/* FAQ */}
      <section id="faq" aria-labelledby="faq-title" className="py-16 sm:py-24 bg-white scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader id="faq-title" eyebrow={t('faq.eyebrow')} title={t('faq.title')} />
          <Accordion type="single" collapsible className="rounded-lg border border-slate-200 bg-white px-5 sm:px-6 shadow-sm">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`q${i}`} className={i === faqs.length - 1 ? 'border-b-0' : ''}>
                <AccordionTrigger className="text-left text-base font-semibold text-brand-900 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Join: one email to us is all it takes; every CTA scrolls here */}
      <section id="join" aria-labelledby="join-title" className="py-16 sm:py-24 bg-brand-50 scroll-mt-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <h2 id="join-title" className="text-3xl sm:text-4xl font-bold text-brand-900 tracking-tight [text-wrap:balance]">{t('join.title')}</h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{t('join.description')}</p>
          <ul className="mt-5 mb-8 flex flex-wrap justify-center gap-2" aria-label={t('join.promisesLabel')}>
            {promises.map(p => (
              <li key={p} className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-white px-3 py-1 text-sm font-medium text-brand-800">
                <Check className="w-3.5 h-3.5 text-action-500" aria-hidden />{p}
              </li>
            ))}
          </ul>
          <EmailUsCard
            subject={t('join.subject')}
            body={t('join.body')}
            buttonLabel={t('join.button')}
            copyLabel={t('join.copy')}
            copiedLabel={t('join.copied')}
            note={t('join.note')}
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
